import moment from 'moment';
import isFeatureEnabled, { Feature } from 'utils/isFeatureEnabled';

/**
 * What counts as a row waiting for the user in the message center.
 *
 * The drawer builds its list from these tests and every unread badge counts
 * the same rows, so a message can never be announced by a badge that the
 * drawer then refuses to show. Kept free of the store so the rule can be
 * tested on its own; the hooks that feed it live in messageCenterScope.js.
 */

// The message center lists what is still waiting for the user, not an archive
// of everything ever said. An entry stays only while it has unread messages
// that arrived inside this window: opening it zeroes the unread count so it
// leaves at once, and anything older ages out by itself. Nothing is lost:
// what ages out stays reachable through the search box, and the banner above
// the list offers to bring it straight back.
export const UNREAD_WINDOW_DAYS = 14;

export const unreadWindowCutoff = () =>
  moment().subtract(UNREAD_WINDOW_DAYS, 'days').toISOString();

// A null cutoff drops the age test — that is how the banner reveals what it
// counted. Timestamps are ISO-8601 UTC from the API, so they compare as
// strings, the same assumption the drawer's sorting makes. A missing one is a
// data gap rather than an old message, and hiding a genuinely unread message
// is the worse failure, so treat it as fresh.
export const isFreshUnread = (unreadCount, lastMessageAt, cutoff) =>
  unreadCount > 0 && (!cutoff || !lastMessageAt || lastMessageAt >= cutoff);

// Mirrors showEmailButton in the drawer's list: the thread rows that draw their
// project's email badge.
export const drawsProjectEmail = (thread, project, permissions) =>
  !thread.isSubthread &&
  !thread.directRecipient &&
  isFeatureEnabled(Feature.PROJECT_EMAIL, project) &&
  !!permissions[project.id];

export const isEmployerEmailWaiting = (project, sources, cutoff) =>
  isFreshUnread(
    sources.unreadMessages[project.projectKey],
    sources.emailLastMessageAt[project.projectKey],
    cutoff
  );

// A thread row earns its place on its own unread chat, or on its project's
// unread email — for anything but an employer project that row is the only
// place the email badge is drawn, so filtering on chat alone hid those messages
// outright. Employer projects are left out because they get an email row of
// their own, and rescuing the thread too would draw the same badge twice.
export const isThreadWaiting = (thread, project, sources, cutoff) =>
  isFreshUnread(thread.unreadMessagesCount, thread.lastMessageAt, cutoff) ||
  (project.type !== 'EMPLOYER' &&
    drawsProjectEmail(thread, project, sources.projectEmailPermissions) &&
    isEmployerEmailWaiting(project, sources, cutoff));

/**
 * How many rows the message center would list — the number every unread badge
 * shows. Counting anything else is how a badge came to announce messages the
 * drawer had aged out, scoped away, or had no project for.
 */
export const countWaitingRows = ({
  threads,
  projects,
  employerProjectsWithEmail,
  sources,
  cutoff,
}) => {
  let count = 0;
  for (const thread of Object.values(threads)) {
    const project = projects[thread.projectId];
    if (!project) continue;
    if (isThreadWaiting(thread, project, sources, cutoff)) count++;
  }
  for (const project of employerProjectsWithEmail) {
    if (isEmployerEmailWaiting(project, sources, cutoff)) count++;
  }
  return count;
};
